import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  redirect,
  retainSearchParams,
} from '@tanstack/react-router'

import { useAuth } from '@/stores/auth'

import { TitleSync } from '@/components/TitleSync'
import { LoginPage } from '@/pages/LoginPage'
import { CompanySelectPage } from '@/pages/CompanySelectPage'
import { UsersPage } from '@/pages/UsersPage'
import { MainShell } from '@/pages/MainShell'
import { ConvertShell } from '@/pages/ConvertShell'
import { ConvertTab } from '@/pages/ConvertTab'
import { PoMappingsTab } from '@/pages/PoMappingsTab'
import { DeptMappingsTab } from '@/pages/DeptMappingsTab'
import { PartnersShell } from '@/pages/PartnersShell'
import { PartnersTab } from '@/pages/PartnersTab'
import { PartnerGroupsTab } from '@/pages/PartnerGroupsTab'
import { ItemsTab } from '@/pages/ItemsTab'
import { PricesTab } from '@/pages/PricesTab'
import { DeliveryShell } from '@/pages/DeliveryShell'
import { DeliveryTab } from '@/pages/DeliveryTab'
import { DeliveryByPartnerTab } from '@/pages/DeliveryByPartnerTab'
import { DeliveryByItemTab } from '@/pages/DeliveryByItemTab'
import { SalesReportTab } from '@/pages/SalesReportTab'
import { PurchaseRegisterTab } from '@/pages/PurchaseRegisterTab'
import { ReceivingShell } from '@/pages/ReceivingShell'
import { ReceivingTab } from '@/pages/ReceivingTab'
import { ReceivingByPartnerTab } from '@/pages/ReceivingByPartnerTab'
import { ReceivingByItemTab } from '@/pages/ReceivingByItemTab'
import { QuoteShell } from '@/pages/QuoteShell'
import { QuoteConvertTab } from '@/pages/QuoteConvertTab'
import { ItemMappingsTab } from '@/pages/ItemMappingsTab'
import { CollectionsTab } from '@/pages/CollectionsTab'
import { PaymentsTab } from '@/pages/PaymentsTab'
import { InvoicePrintPage } from '@/pages/InvoicePrintPage'
import { LedgerTab } from '@/pages/LedgerTab'
import { VerifyTab } from '@/pages/VerifyTab'

// 회사는 URL 쿼리 ?company=id 로 창별 관리. retainSearchParams 로 모든 이동에
// 자동 유지되어 링크마다 회사를 넘길 필요가 없다.
interface RootSearch { company?: number; [k: string]: unknown }

const rootRoute = createRootRoute({
  // company 만 정규화하고 나머지 검색파라미터(order/from/to 등)는 그대로 통과.
  validateSearch: (s: Record<string, unknown>): RootSearch => {
    const n = s.company != null ? Number(s.company) : NaN
    return { ...s, company: Number.isFinite(n) && n > 0 ? n : undefined }
  },
  search: { middlewares: [retainSearchParams(['company'])] },
  component: () => (
    <>
      <TitleSync />
      <Outlet />
    </>
  ),
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const companySelectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/company-select',
  beforeLoad: () => {
    if (!useAuth.getState().token) throw redirect({ to: '/login' })
  },
  component: CompanySelectPage,
})

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  beforeLoad: () => {
    const { token, user } = useAuth.getState()
    if (!token) throw redirect({ to: '/login' })
    if (!user?.is_master) throw redirect({ to: '/company-select' })  // 마스터 전용
  },
  component: UsersPage,
})

const mainRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/main',
  beforeLoad: ({ search }) => {
    if (!useAuth.getState().token) throw redirect({ to: '/login' })
    if (!(search as RootSearch).company) throw redirect({ to: '/company-select' })
  },
  component: MainShell,
})

// 발주서 변환 — pathless 레이아웃으로 변환 + 코드매칭 묶음 (URL 세그먼트 추가 없음).
// path:'/' 레이아웃은 형제 라우트와 충돌 여지가 있어 pathless(id) 사용.
const convertParentRoute = createRoute({
  getParentRoute: () => mainRoute, id: 'convertShell', component: ConvertShell,
})
const convertIndexRoute = createRoute({
  getParentRoute: () => convertParentRoute, path: '/', component: ConvertTab,
})
const poMappingsRoute = createRoute({
  getParentRoute: () => convertParentRoute, path: '/code-match', component: PoMappingsTab,
})
const deptMappingsRoute = createRoute({
  getParentRoute: () => convertParentRoute, path: '/dept-match', component: DeptMappingsTab,
})
// 거래처관리 — sub-tab 으로 개별 + 상위 두 view 묶음
const partnersParentRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/partners', component: PartnersShell,
})
const partnersListRoute = createRoute({
  getParentRoute: () => partnersParentRoute, path: '/', component: PartnersTab,
})
const partnerGroupsRoute = createRoute({
  getParentRoute: () => partnersParentRoute, path: '/groups', component: PartnerGroupsTab,
})
const itemsRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/items', component: ItemsTab,
})
const pricesRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/prices', component: PricesTab,
})

// 출고처리 — sub-tab 으로 4개 view 묶음
const deliveryParentRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/delivery', component: DeliveryShell,
})
const deliveryOrdersRoute = createRoute({
  getParentRoute: () => deliveryParentRoute, path: '/', component: DeliveryTab,
})
const deliveryByPartnerRoute = createRoute({
  getParentRoute: () => deliveryParentRoute, path: '/by-partner', component: DeliveryByPartnerTab,
})
const deliveryByItemRoute = createRoute({
  getParentRoute: () => deliveryParentRoute, path: '/by-item', component: DeliveryByItemTab,
})
const deliverySalesReportRoute = createRoute({
  getParentRoute: () => deliveryParentRoute, path: '/sales-report', component: SalesReportTab,
})
const deliveryPurchaseRegisterRoute = createRoute({
  getParentRoute: () => deliveryParentRoute, path: '/purchase-register', component: PurchaseRegisterTab,
})

// 견적서 변환 — sub-tab 으로 2개 view 묶음
const quoteParentRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/quote', component: QuoteShell,
})
const quoteConvertRoute = createRoute({
  getParentRoute: () => quoteParentRoute, path: '/', component: QuoteConvertTab,
})
const quoteMappingsRoute = createRoute({
  getParentRoute: () => quoteParentRoute, path: '/mappings', component: ItemMappingsTab,
})

// 입고처리 — sub-tab 으로 3개 view 묶음
const receivingParentRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/receiving', component: ReceivingShell,
})
const receivingOrdersRoute = createRoute({
  getParentRoute: () => receivingParentRoute, path: '/', component: ReceivingTab,
})
const receivingByPartnerRoute = createRoute({
  getParentRoute: () => receivingParentRoute, path: '/by-partner', component: ReceivingByPartnerTab,
})
const receivingByItemRoute = createRoute({
  getParentRoute: () => receivingParentRoute, path: '/by-item', component: ReceivingByItemTab,
})

const collectionsRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/collections', component: CollectionsTab,
})
const paymentsRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/payments', component: PaymentsTab,
})
// 거래명세서는 인쇄 전용 창(사이드바 없음). rootRoute 하위에 별도 라우트로 배치.
const invoicePrintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoice-print',
  beforeLoad: ({ search }) => {
    if (!useAuth.getState().token) throw redirect({ to: '/login' })
    if (!(search as RootSearch).company) throw redirect({ to: '/company-select' })
  },
  component: InvoicePrintPage,
})
const ledgerRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/ledger', component: LedgerTab,
})
const verifyRoute = createRoute({
  getParentRoute: () => mainRoute, path: '/verify', component: VerifyTab,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // 로그인 후엔 항상 회사 선택부터 (회사는 URL 로 고르므로 자동 진입 없음).
    if (!useAuth.getState().token) throw redirect({ to: '/login' })
    throw redirect({ to: '/company-select' })
  },
  component: () => null,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  companySelectRoute,
  usersRoute,
  invoicePrintRoute,
  mainRoute.addChildren([
    convertParentRoute.addChildren([
      convertIndexRoute,
      poMappingsRoute,
      deptMappingsRoute,
    ]),
    partnersParentRoute.addChildren([
      partnersListRoute,
      partnerGroupsRoute,
    ]),
    itemsRoute,
    pricesRoute,
    deliveryParentRoute.addChildren([
      deliveryOrdersRoute,
      deliveryByPartnerRoute,
      deliveryByItemRoute,
      deliverySalesReportRoute,
      deliveryPurchaseRegisterRoute,
    ]),
    receivingParentRoute.addChildren([
      receivingOrdersRoute,
      receivingByPartnerRoute,
      receivingByItemRoute,
    ]),
    quoteParentRoute.addChildren([
      quoteConvertRoute,
      quoteMappingsRoute,
    ]),
    collectionsRoute,
    paymentsRoute,
    ledgerRoute,
    verifyRoute,
  ]),
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}
